import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { Engine } from '../engine/engine';
import { environment } from '../config/editor-environment';
import { KeyboardShortcutsService } from './keyboard-shortcuts.service';

/** Minimal shape of any TrackballControls-like object that can receive a user mouse preset. */
interface TrackballLike {
  mouseButtons: {
    LEFT?: number;
    MIDDLE?: number;
    RIGHT?: number;
    SHIFTMIDDLE?: number;
    SHIFTRIGHT?: number;
    CTRLMIDDLE?: number;
    DOUBLEMIDDLE?: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class SettingsAndPresetsService {
  private http = inject(HttpClient);
  public keyboardShortcuts = inject(KeyboardShortcutsService);

  // Mouse Settings
  public currentMousePreset: any;
  // Graphics Settings
  public currentGraphicsSettings: any;

  // Engine reference - set externally by SessionService
  private engine?: Engine;

  /**
   * Secondary TrackballControls instances (e.g. per-tile controls in the
   * multi-MF compare view) that want to receive the same mouse preset as
   * the main engine controls. Populated via `registerExternalControls`.
   */
  private externalControls = new Set<TrackballLike>();

  setEngine(engine: Engine): void {
    this.engine = engine;
  }

  // Methods not used in cloud version
  public initGraphicsPreset(): void {
    this.getCurrentGraphicsPreset();
  }

  public getCurrentGraphicsPreset(): void {}

  public setCurrentGraphicsSettings(): void {
    setTimeout(async () => await this.setUserSettings());
  }

  public async changeGraphicsSettings(): Promise<void> {
    this.engine?.windowResize();
    this.setCurrentGraphicsSettings();
  }

  // Mouse
  private lastMiddleClickTime = 0;
  private DOUBLE_CLICK_INTERVAL = 300;
  private auxclickHandler: ((event: MouseEvent) => void) | null = null;

  public initMousePreset(): void {
    // Gets the current preset or applies default one
    this.getCurrentMousePreset();

    // Remove previous listener if any to prevent stacking
    if (this.auxclickHandler && this.engine?.canvas) {
      this.engine.canvas.removeEventListener('auxclick', this.auxclickHandler);
    }

    this.auxclickHandler = (event: MouseEvent) => {
      if (event.button === 1) {
        // 1 = middle mouse button
        const currentTime = Date.now();
        if (currentTime - this.lastMiddleClickTime < this.DOUBLE_CLICK_INTERVAL) {
          if (this.currentMousePreset['DOUBLEMIDDLE'] === 1) {
            this.engine?.viewHelper?.fitCameraToObject(this.engine?.group!, 1);
          }
          this.lastMiddleClickTime = 0;
        } else {
          // Store timestamp of first click
          this.lastMiddleClickTime = currentTime;
        }
      }
    };

    this.engine?.canvas.addEventListener('auxclick', this.auxclickHandler);
  }

  public getCurrentMousePreset(): void {
    this.setCurrentMousePreset();
  }

  public setCurrentMousePreset(): void {
    if (this.engine?.controls) {
      this.applyMousePresetToControls(this.engine.controls);
    }
    for (const controls of this.externalControls) {
      this.applyMousePresetToControls(controls);
    }
    this.engine?.refreshOrbitPivotHint();

    setTimeout(async () => await this.setUserSettings());
  }

  /**
   * Apply the current mouse preset to any TrackballControls-like object.
   * Missing / negative preset slots map to -1 (disabled), matching the
   * existing behavior for the main engine controls.
   */
  public applyMousePresetToControls(controls: TrackballLike): void {
    const preset = this.currentMousePreset;
    if (!preset || !controls?.mouseButtons) return;
    controls.mouseButtons.LEFT = preset.LEFT >= 0 ? preset.LEFT : -1;
    controls.mouseButtons.MIDDLE = preset.MIDDLE >= 0 ? preset.MIDDLE : -1;
    controls.mouseButtons.RIGHT = preset.RIGHT >= 0 ? preset.RIGHT : -1;
    controls.mouseButtons.SHIFTMIDDLE = preset.SHIFTMIDDLE >= 0 ? preset.SHIFTMIDDLE : -1;
    controls.mouseButtons.SHIFTRIGHT = preset.SHIFTRIGHT >= 0 ? preset.SHIFTRIGHT : -1;
    controls.mouseButtons.CTRLMIDDLE = preset.CTRLMIDDLE >= 0 ? preset.CTRLMIDDLE : -1;
    controls.mouseButtons.DOUBLEMIDDLE = preset.DOUBLEMIDDLE >= 0 ? preset.DOUBLEMIDDLE : -1;
  }

  /**
   * Register a secondary TrackballControls instance (e.g. a compare-view
   * tile's own controls) so it receives the current preset immediately and
   * any future preset changes. Callers MUST pair this with
   * `unregisterExternalControls` on teardown.
   */
  public registerExternalControls(controls: TrackballLike): void {
    this.externalControls.add(controls);
    this.applyMousePresetToControls(controls);
  }

  public unregisterExternalControls(controls: TrackballLike): void {
    this.externalControls.delete(controls);
  }

  /**
   * Attach the DOUBLEMIDDLE "fit camera" auxclick handler to an arbitrary
   * canvas (e.g. a compare-view tile). Returns a teardown function the
   * caller invokes on destroy. The handler only fires when the user's
   * preset has `DOUBLEMIDDLE === 1`, matching the main engine behavior.
   */
  public attachDoubleMiddleFitHandler(
    canvas: HTMLCanvasElement,
    onFit: () => void,
  ): () => void {
    let lastTime = 0;
    const handler = (event: MouseEvent): void => {
      if (event.button !== 1) return;
      const now = Date.now();
      if (now - lastTime < this.DOUBLE_CLICK_INTERVAL) {
        if (this.currentMousePreset?.DOUBLEMIDDLE === 1) onFit();
        lastTime = 0;
      } else {
        lastTime = now;
      }
    };
    canvas.addEventListener('auxclick', handler);
    return () => canvas.removeEventListener('auxclick', handler);
  }

  public changeMousePresetSingle(button: any, action: any): void {
    this.currentMousePreset[button] = action;
    this.setCurrentMousePreset();
  }

  // Local Storage
  public getSettingBoolean(key: string): boolean | null {
    const value = localStorage.getItem(key);
    if (value) return value === 'yes';
    return null;
  }

  public setSetting(key: string, value: string): void {
    localStorage.setItem(key, value);
  }

  // User Settings
  async getUserSettings(): Promise<any> {
    let url = `${environment.API_ADDRESS}/user/settings`;
    return lastValueFrom(this.http.get(url));
  }

  async setUserSettings(): Promise<any> {
    let url = `${environment.API_ADDRESS}/user/settings`;
    return lastValueFrom(
      this.http.post(url, {
        graphics: this.currentGraphicsSettings,
        mouse: this.currentMousePreset,
        keyboardShortcuts: this.keyboardShortcuts.exportForSync()
      })
    );
  }
}
